import type { DashboardActionCard, DayRange } from "@estelle/domain";
import { actionCard } from "@estelle/domain";

import { encodeFilter } from "@/components/filters/url";
import { DEFAULT_CHART, encodeChart } from "@/modules/reports/orderChart";

/**
 * Where a dashboard card goes (#528): the list it counts, with the filter it
 * counted by, or the report for the same period.
 *
 * The filter comes from the server's answer, which is the stored filter of the
 * shared view behind the card, so the list opens on exactly the rows the number
 * was taken from.
 */
export interface CardLink {
  to: string;
  search: { filter?: string; chart?: string };
}

export function actionCardLink(card: DashboardActionCard): CardLink | null {
  if (card.status !== "live") return null;
  const filter = card.filter ? encodeFilter(card.filter) : undefined;
  return {
    to: actionCard(card.key).path,
    search: filter ? { filter } : {},
  };
}

export type ReportGroup = "sales" | "delivery" | "mix" | "speed";

/** The report a performance group opens, on the dates of the period shown. */
export function reportLink(group: ReportGroup, range: DayRange): CardLink {
  switch (group) {
    case "sales": {
      // The card counts every order placed, cancelled ones included, and the
      // report leaves them out unless asked (#506); asking keeps both equal.
      const chart = encodeChart({
        ...DEFAULT_CHART,
        range,
        includeCancelled: true,
      });
      return { to: "/reports", search: chart ? { chart } : {} };
    }
    case "mix": {
      // Orders by payment, cancelled ones included: the cancellation rate and
      // the COD share both count every order placed.
      const chart = encodeChart({
        ...DEFAULT_CHART,
        range,
        splitBy: "payment",
        includeCancelled: true,
      });
      return { to: "/reports/orders", search: chart ? { chart } : {} };
    }
    case "delivery":
    case "speed": {
      const chart = encodeChart({ ...DEFAULT_CHART, range });
      return { to: "/reports/delivery", search: chart ? { chart } : {} };
    }
  }
}
